import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Status pill / badge — color + icon pairing per MASTER_PROMPT §1.6.
// Always pair with text or an icon; never color-only.
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-xs font-semibold leading-none",
  {
    variants: {
      variant: {
        // Neutral / draft — text-3 on card-2 surface.
        default: "bg-bg-card2 text-text-2 border border-border-soft",
        outline: "border border-border text-text-2",
        muted: "bg-bg-card2 text-text-3 border border-border-soft",
        // Status semantics
        info: "bg-info-soft text-info border border-info/30",
        success: "bg-success-soft text-success border border-success/30",
        warn: "bg-warn-soft text-warn border border-warn/35",
        danger: "bg-danger-soft text-danger border border-danger/35",
        // AI / Lumen — purple, distinct from product accent.
        ai: "bg-ai-soft text-ai border border-ai/30",
        // Indigo — when something is the active selection in the document
        // (type pill, current view, "AI-but-not-AI" treatment).
        accent: "bg-accent-soft text-accent border border-accent/35",
        // Legacy alias used in older components.
        "accent-purple": "bg-ai-soft text-ai border border-ai/30",
      },
      size: {
        sm: "text-[10px] px-1.5 py-[2px]",
        md: "text-xs px-2 py-[3px]",
        lg: "text-sm px-2.5 py-[5px]",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}
